/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CallLog } from '../../../../types/call-logs';
import AudioPlayer from '../../../audio-player/audio-player';
import { getSignedUrlForCallRecordingRequest } from '../../../../extra-actions';
import { RootState } from '../../../../../../store/root-reducer';
import CopyButton from '../../../../../../shared/components/copy-button';
import { Placement } from '../../../../../../shared/design-system/components/overlay';
import './call-logs-single-call-detail.scss';
import InlineMentionEditor from '../../../../../prospect/components/prospect-kanban/Inline-mention-editor';
import {
  createCallLogsNotePayload,
  updateCallLogsNotePayload,
  visibility,
} from '../../../../types';
import {
  AttachmentMetaDataType,
  AttachmentType,
  AttachmentUploadStatus,
  AttachmentWithPreSignedUrl,
} from '../../../../../../shared/editor/types';
import {
  AttachmentDeletePayload,
  AttachmentUploadPayload,
  AttachmentUploadResponse,
} from '../../../../../sequence/types';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import {
  ResponseErrorWithHandled,
  ResponseSuccess,
} from '../../../../../../shared/types/response';
import { isContentExceeds2MB } from '../../../../../prospect/components/prospect-list/components/prospect-details/prospect-details-modal/tasks-list-tab/utils/helper';
import CallLogsDetailCallOutcomeSelect from './call-logs-detail-call-outcome-select';
import CallLogsOutboundDetailSections from './call-logs-outbound-detail-sections';
import {
  executeOnRequestStatus,
  getIsRequestPending,
  toTelHref,
} from '../../../../../../shared/utils';
import {
  formatTranscriptionItemsAsPlainText,
  parseCallTranscriptionToItems,
} from '../../utils/helpers';

export interface CallLogsSingleCallDetailProps {
  row?: CallLog;
  callLogsProspectProfileSection?: any;
  handleSendAttachmentUploadRequest?: (
    payload: AttachmentUploadPayload,
  ) => void;
  handleResetUploadAttachmentRequest?: () => void;
  handleSendDeleteAttachmentRequest?: (
    payload: AttachmentDeletePayload,
  ) => void;
  handleResetDeleteAttachmentRequest?: () => void;
  uploadAttachmentRequestStatus?: RequestStatus;
  uploadAttachmentRequestError?: ResponseErrorWithHandled;
  uploadedAttachment?: AttachmentUploadResponse;
  deleteAttachmentRequestStatus?: RequestStatus;
  deleteAttachmentRequestError?: ResponseErrorWithHandled;
  deletedAttachmentId?: number;
  handleCreateCallLogsNoteRequest?: (
    payload: createCallLogsNotePayload,
  ) => void;
  handleUpdateCallLogsNoteRequest?: (
    payload: updateCallLogsNotePayload,
  ) => void;
  updateCallLogsNoteRequestStatus?: RequestStatus;
  resetUpdateCallLogsNoteRequest?: () => void;
  handleGetPhoneCallMentionableUsersRequest?: (callId: number) => void;
  sendAssignCallOutcomeRequest?: (
    outcomeId: number,
    dialerCallLogId: number,
  ) => void;
  userTimeZone?: string;
  callLogsProspectDrawerListing?: boolean;
  getPhoneCallMentionableUsersResponse?: ResponseSuccess;
  createCallLogsNoteRequestStatus?: RequestStatus;
  resetCreateCallLogsNoteRequest?: () => void;
  getPhoneNumberDetailsRequestStatus?: RequestStatus;
  resetGetPhoneNumberDetailsRequest?: () => void;
}

const transformApiAttachmentToAttachmentType = (
  apiAttachment: any,
): AttachmentWithPreSignedUrl => {
  const sizeNumber = apiAttachment?.size
    ? parseInt(String(apiAttachment.size), 10)
    : 0;
  const id = apiAttachment?.id;
  return {
    attachmentId: id,
    attachmentIdentifier: id != null ? `existing-call-note-${id}` : undefined,
    status: AttachmentUploadStatus.Existing,
    file: {
      name: apiAttachment?.name || '',
      type: apiAttachment?.mimeType || '',
      size: sizeNumber,
    },
    attachmentSize: sizeNumber,
    preSignedUrl: apiAttachment?.preSignedUrl,
  };
};

const transformApiAttachments = (apiAttachments: any[]): AttachmentType[] => {
  if (!apiAttachments || !Array.isArray(apiAttachments)) {
    return [];
  }
  return apiAttachments.map(transformApiAttachmentToAttachmentType);
};

const coercePhoneDisplay = (value: unknown): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'number' in value) {
    const n = (value as { number?: unknown }).number;
    if (typeof n === 'string') return n;
    if (n != null) return String(n);
  }
  if (typeof value === 'number') return String(value);
  return '';
};

const PhoneTelText: React.FC<{ className?: string; text: string }> = ({
  className = 'regular-2 gray-txt-9',
  text,
}) => {
  const tel = toTelHref(text);
  if (!text) {
    return <span className={className} />;
  }
  if (tel) {
    return (
      <a
        href={tel}
        className={`${className} bs-call-logs-missed-call-detail__tel-link`}
      >
        {text}
      </a>
    );
  }
  return <span className={className}>{text}</span>;
};

const PhoneTelTextWithFriendlyName: React.FC<{
  className?: string;
  friendlyName?: string | null;
  text: string;
}> = ({ className = 'regular-2 gray-txt-9', friendlyName, text }) => {
  if (friendlyName) {
    return (
      <>
        {friendlyName} ( <PhoneTelText className={className} text={text} /> )
      </>
    );
  }

  return <PhoneTelText className={className} text={text} />;
};

const CallLogsSingleCallDetail: React.FC<CallLogsSingleCallDetailProps> = ({
  row,
  callLogsProspectProfileSection,
  handleSendAttachmentUploadRequest,
  handleResetUploadAttachmentRequest,
  handleSendDeleteAttachmentRequest,
  handleResetDeleteAttachmentRequest,
  uploadAttachmentRequestStatus,
  uploadAttachmentRequestError,
  uploadedAttachment,
  deleteAttachmentRequestStatus,
  deleteAttachmentRequestError,
  deletedAttachmentId,
  handleCreateCallLogsNoteRequest,
  handleUpdateCallLogsNoteRequest,
  handleGetPhoneCallMentionableUsersRequest,
  sendAssignCallOutcomeRequest,
  userTimeZone,
  callLogsProspectDrawerListing,
  getPhoneCallMentionableUsersResponse,
  createCallLogsNoteRequestStatus,
  updateCallLogsNoteRequestStatus,
  // resetUpdateCallLogsNoteRequest,
  getPhoneNumberDetailsRequestStatus,
  resetGetPhoneNumberDetailsRequest,
}) => {
  const dispatch = useDispatch();
  const signedUrl = useSelector(
    (state: RootState) =>
      state?.dialer?.getSignedUrlForCallRecordingResponses?.[row?.callId]
        ?.signedUrl ?? null,
  );
  const callType = callLogsProspectProfileSection?.type;
  const missedCallTypes = [
    'missed-with-voicemail',
    'missed',
    'incoming',
    'inbound',
  ];

  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
  const [selectedVisibilityKey, setSelectedVisibilityKey] = useState<string>(
    'public',
  );
  const [value, setValue] = useState('');
  const [showError, setShowError] = useState('');
  const [attachments, setAttachments] = useState<AttachmentType[]>([]);
  const [attachmentsMeta, setAttachmentsMeta] = useState<
    AttachmentMetaDataType[]
  >([]);
  const [isEditingNote, setIsEditingNote] = useState(true);
  /** True after Save until parent passes note from API — drives read-only UI when props still empty */
  const [pendingSavedNote, setPendingSavedNote] = useState(false);
  const lastHydratedNoteKeyRef = useRef<string>('');

  const noteResponse =
    callLogsProspectProfileSection?.noteResponse ||
    callLogsProspectProfileSection?.note;

  const hasExistingNoteFromProps = useMemo(
    () =>
      Boolean(
        noteResponse?.id ||
          (typeof noteResponse?.content === 'string' &&
            noteResponse?.content?.trim()?.length > 0) ||
          (noteResponse?.attachments && noteResponse?.attachments?.length > 0),
      ),
    [
      noteResponse?.id,
      noteResponse?.content,
      noteResponse?.attachments?.length,
    ],
  );

  const hasExistingNote = hasExistingNoteFromProps || pendingSavedNote;

  useEffect(() => {
    setPendingSavedNote(false);
  }, [row?.callId]);

  useEffect(() => {
    const callId = String(row?.callId ?? '');
    const noteRes =
      callLogsProspectProfileSection?.noteResponse ||
      callLogsProspectProfileSection?.note;
    const noteKey = `${callId}|${noteRes?.id ?? 'none'}|${
      (noteRes?.content || '').length
    }|${noteRes?.attachments?.length ?? 0}`;

    if (lastHydratedNoteKeyRef.current === noteKey) {
      return;
    }
    lastHydratedNoteKeyRef.current = noteKey;

    if (noteRes) {
      setValue(noteRes?.content ?? '');
      setSelectedVisibilityKey(noteRes?.visibility ?? 'public');
      setAttachments(
        noteRes?.attachments?.length
          ? transformApiAttachments(noteRes?.attachments)
          : [],
      );
    } else {
      setValue('');
      setSelectedVisibilityKey('public');
      setAttachments([]);
    }

    const hasNote = Boolean(
      noteRes?.id ||
        (typeof noteRes?.content === 'string' &&
          noteRes?.content?.trim()?.length > 0) ||
        (noteRes?.attachments && noteRes?.attachments?.length > 0),
    );

    if (hasNote) {
      setPendingSavedNote(false);
      setIsEditingNote(false);
    } else if (!pendingSavedNote) {
      setIsEditingNote(true);
    } else {
      setIsEditingNote(false);
    }
  }, [
    row?.callId,
    callLogsProspectProfileSection?.noteResponse,
    callLogsProspectProfileSection?.note,
    pendingSavedNote,
  ]);

  const handleNoteFooterCancel = useCallback(() => {
    setPendingSavedNote(false);
    const nr =
      callLogsProspectProfileSection?.noteResponse ||
      callLogsProspectProfileSection?.note;
    if (nr) {
      setValue(nr.content ?? '');
      setSelectedVisibilityKey(nr.visibility ?? 'public');
      setAttachments(
        nr.attachments?.length ? transformApiAttachments(nr.attachments) : [],
      );
    } else {
      setValue('');
      setSelectedVisibilityKey('public');
      setAttachments([]);
    }
    setIsEditingNote(false);
  }, [
    callLogsProspectProfileSection?.noteResponse,
    callLogsProspectProfileSection?.note,
  ]);

  const handlePlayWithoutSrc = useCallback(async () => {
    const callIdNumber = Number(row?.callId);
    if (!Number.isFinite(callIdNumber)) return;

    setShouldAutoPlay(true);
    setIsLoadingAudio(true);
    try {
      const action: any = await dispatch(
        getSignedUrlForCallRecordingRequest(callIdNumber) as any,
      );
      const payload = action?.payload?.payload ?? action?.payload;
      if (!payload?.signedUrl) {
        setIsLoadingAudio(false);
      }
    } catch {
      setIsLoadingAudio(false);
    }
  }, [dispatch, row?.callId]);

  const audioSrc = signedUrl || row?.recordingUrl || null;
  const showVoicemailBlock =
    callLogsProspectProfileSection?.isVoicemailDetected;

  const { transcription, isTranscriptionInProgress } = row;
  const transcriptionPending = isTranscriptionInProgress === true;
  const voicemailTranscriptCopyText = formatTranscriptionItemsAsPlainText(
    parseCallTranscriptionToItems(transcription),
  );

  const handleVisibilityChange = (key: string) => {
    setSelectedVisibilityKey(key);
  };

  const handleSave = () => {
    if (isContentExceeds2MB(value || '')) {
      setShowError('Content size cannot exceed 2MB');
      return;
    }
    // Set flag to prevent draft save from triggering
    attachments.forEach((attachment) => {
      if (attachment.status === AttachmentUploadStatus.Uploading) {
        const attachmentMeta = attachmentsMeta?.find(
          (item) =>
            item?.attachmentIdentifier === attachment?.attachmentIdentifier,
        );
        if (attachmentMeta?.source) {
          attachmentMeta.source.cancel('Upload has been cancelled.');
        }
      }
    });
    setAttachmentsMeta([]);

    const attachmentIds =
      attachments?.map((attachment) => attachment?.attachmentId) ?? [];

    if (noteResponse?.id) {
      const payload: updateCallLogsNotePayload = {
        noteId: Number(noteResponse.id),
        callId: Number(row?.callId),
        content: value,
        visibility: selectedVisibilityKey as visibility,
        status: 1,
        attachmentIds,
      };
      handleUpdateCallLogsNoteRequest(payload);
    } else {
      const payload: createCallLogsNotePayload = {
        callId: Number(row?.callId),
        content: value,
        visibility: selectedVisibilityKey as visibility,
        status: 1,
        attachmentIds,
      };
      handleCreateCallLogsNoteRequest(payload);
    }

    setPendingSavedNote(true);
    setIsEditingNote(false);

    // Extract attachment IDs from attachments array
    // const attachmentIdsFromAttachments = attachments
    //   .map((attachment) => attachment?.attachmentId)
    //   .filter((id): id is number => id !== undefined && id !== null);
  };

  const handleAttachmentsChange = (
    updatedAttachments: AttachmentType[],
  ): void => {
    setAttachments(updatedAttachments);
  };

  const handleAttachmentStatusChange = (updatedAttachment: AttachmentType) => {
    // If editing a note, update editingNoteAttachments; otherwise update attachments (for new note)

    setAttachments((prevAttachments) =>
      prevAttachments.map((attachment) =>
        (attachment.attachmentIdentifier &&
          updatedAttachment.attachmentIdentifier &&
          attachment.attachmentIdentifier ===
            updatedAttachment.attachmentIdentifier) ||
        (attachment.attachmentId != null &&
          attachment.attachmentId === updatedAttachment.attachmentId)
          ? {
              ...attachment,
              ...updatedAttachment,
              status:
                updatedAttachment.status ||
                AttachmentUploadStatus.UploadSuccess,
            }
          : attachment,
      ),
    );
  };

  useEffect(() => {
    executeOnRequestStatus({
      status: createCallLogsNoteRequestStatus,
      onSuccess: () => {
        setIsEditingNote(false);
      },
      onFailed: () => {
        setPendingSavedNote(false);
        setIsEditingNote(true);
      },
    });
  }, [createCallLogsNoteRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: updateCallLogsNoteRequestStatus,
      onSuccess: () => {
        setIsEditingNote(false);
      },
      onFailed: () => {
        setPendingSavedNote(false);
        setIsEditingNote(true);
      },
    });
  }, [updateCallLogsNoteRequestStatus]);

  useEffect(() => {
    if (getPhoneCallMentionableUsersResponse?.payload?.length === 0) {
      handleGetPhoneCallMentionableUsersRequest(row?.id);
    }
  }, [getPhoneCallMentionableUsersResponse]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getPhoneNumberDetailsRequestStatus,
      onSuccess: () => {
        resetGetPhoneNumberDetailsRequest();
      },
      onFailed: () => {
        resetGetPhoneNumberDetailsRequest();
      },
    });
  }, [getPhoneNumberDetailsRequestStatus]);

  const calledFromOrToPhone = coercePhoneDisplay(
    missedCallTypes?.includes(callType)
      ? callLogsProspectProfileSection?.fromNumber ||
          callLogsProspectProfileSection?.prospectPhoneNumber
      : callLogsProspectProfileSection?.toNumber ||
          callLogsProspectProfileSection?.prospectPhoneNumber,
  );

  const receivedOnOrViaPhone = coercePhoneDisplay(
    missedCallTypes?.includes(callType)
      ? callLogsProspectProfileSection?.toNumber ||
          callLogsProspectProfileSection?.userPhoneNumber?.number
      : callLogsProspectProfileSection?.fromNumber ||
          callLogsProspectProfileSection?.userPhoneNumber?.number,
  );

  return (
    <div className="bs-call-logs-missed-call-detail">
      {/* Voicemail Details */}
      <div className="bs-call-logs-missed-call-detail__fields">
        <div className="bs-call-logs-missed-call-detail__field">
          <div className="bs-call-logs-missed-call-detail__field-label regular-1 gray-txt-15">
            {missedCallTypes?.includes(callType) ? 'Called from' : 'Called to'}
          </div>
          <div className="bs-call-logs-missed-call-detail__field-value-row">
            <PhoneTelText text={calledFromOrToPhone} />
            {/* {showPrimaryBadge && ( */}
            <span className="bs-call-logs-missed-call-detail__primary-pill regular-1">
              Primary
            </span>
            {/* )} */}
          </div>
        </div>

        <div className="bs-call-logs-missed-call-detail__field">
          <div className="bs-call-logs-missed-call-detail__field-label regular-1 gray-txt-15">
            {missedCallTypes?.includes(callType)
              ? 'Call Received on'
              : 'Call Made Via'}
          </div>
          <div className="regular-2 gray-txt-9 d-flex align-items-center gap-4">
            <PhoneTelTextWithFriendlyName
              friendlyName={
                callLogsProspectProfileSection?.dialerPhoneNumber?.friendlyName
              }
              text={receivedOnOrViaPhone}
            />
            {callLogsProspectProfileSection?.dialerPhoneNumber?.status ===
              5 && <span className="deleted-number-badge">Deleted</span>}
          </div>
        </div>
      </div>

      {/* Voicemail Block */}
      {showVoicemailBlock && callType === 'inbound' && (
        <div className="bs-call-logs-missed-call-detail__voicemail-block">
          <div className="semibold-1 gray-txt-15 bs-call-logs-missed-call-detail__section-title">
            Voicemail
          </div>
          <div className="bs-call-logs-missed-call-detail__player-shell">
            <AudioPlayer
              pauseOnScroll={false}
              defaultDuration={row?.recordingDuration}
              audioSrc={audioSrc}
              className={`voicemail-greeting-audio-player ${
                isLoadingAudio ? 'audio-player-loading' : ''
              }`}
              showPlaybackSpinner={isLoadingAudio}
              onPlayWithoutSrc={handlePlayWithoutSrc}
              autoPlayWhenSrcAvailable={shouldAutoPlay}
              onAutoPlayCompleted={() => {
                setShouldAutoPlay(false);
                setIsLoadingAudio(false);
              }}
            />
          </div>
        </div>
      )}
      {/* Voicemail Transcript Block */}
      {callLogsProspectProfileSection?.dialerPhoneNumberCallDetail
        ?.transcription &&
        !callLogsProspectProfileSection?.dialerPhoneNumberCallDetail
          ?.aiSummary &&
        callType === 'inbound' && (
          <div className="bs-call-logs-missed-call-detail__transcript-block">
            <div className="bs-call-logs-missed-call-detail__transcript-card">
              <div className="bs-call-logs-missed-call-detail__transcript-header">
                <span className="semibold-1 gray-txt-15">
                  Voicemail Transcript
                </span>
                {voicemailTranscriptCopyText ? (
                  <CopyButton
                    text={voicemailTranscriptCopyText}
                    copyTooltipText="Copy"
                    resetDelay={1000}
                    placement={Placement.Bottom}
                    className="bs-call-logs-missed-call-detail__copy-btn"
                  />
                ) : null}
              </div>
              <div className="bs-call-logs-missed-call-detail__transcript-body regular-2 gray-txt-9">
                {transcriptionPending && (
                  <span className="regular-2 gray-txt-11">
                    Transcription in progress…
                  </span>
                )}
                {!transcriptionPending && transcription && (
                  <p className="bs-call-logs-missed-call-detail__transcript-text">
                    {transcription}
                  </p>
                )}
                {!transcriptionPending && !transcription && (
                  <span className="regular-2 gray-txt-11">
                    No transcript yet.
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

      {!callLogsProspectDrawerListing && (
        <div className="bs-call-logs-missed-call-detail__call-outcome-select">
          <label htmlFor="call-outcome-select">Call Outcome</label>
          <CallLogsDetailCallOutcomeSelect
            row={row}
            onChangeCallOutcome={(outcomeId: number, callLogId: number) => {
              sendAssignCallOutcomeRequest(outcomeId, callLogId);
            }}
          />
        </div>
      )}

      {/* Voicemail Note Editor */}
      <div
        className={`bs-call-logs-missed-call-detail__notes-editor ${
          hasExistingNote && !isEditingNote ? 'editing' : ''
        }`}
      >
        <span className="semibold-1 gray-txt-15 font-12">Note</span>
        <InlineMentionEditor
          key={`call-logs-note-editor-${row?.callId ?? 'unknown'}`}
          value={value}
          onEditorChange={(values) => setValue(values)}
          showAddAttachments={true}
          showToolBarContainer={true}
          showFooterToolbarContainer={!hasExistingNote || isEditingNote}
          showFooterVisibilitySelector={!hasExistingNote || isEditingNote}
          showFooterSaveButton={!hasExistingNote || isEditingNote}
          showFooterCancelButton={hasExistingNote && isEditingNote}
          onFooterSave={handleSave}
          onFooterCancel={handleNoteFooterCancel}
          disabled={
            (hasExistingNote && !isEditingNote) ||
            getIsRequestPending(createCallLogsNoteRequestStatus) ||
            getIsRequestPending(updateCallLogsNoteRequestStatus)
          }
          selectedVisibilityKey={selectedVisibilityKey}
          onVisibilityChange={handleVisibilityChange}
          isFooterSaveButtonDisabled={
            value?.trim()?.length === 0 ||
            getIsRequestPending(createCallLogsNoteRequestStatus) ||
            getIsRequestPending(updateCallLogsNoteRequestStatus)
          }
          placeholder={
            selectedVisibilityKey === visibility.Private
              ? 'Visible only to you and people tagged here'
              : 'Visible to everyone in the organisation*'
          }
          attachments={attachments}
          onAttachmentsChange={handleAttachmentsChange} //
          onAttachmentStatusChange={handleAttachmentStatusChange}
          sendAttachmentUploadRequest={handleSendAttachmentUploadRequest} ///
          uploadAttachmentRequestStatus={uploadAttachmentRequestStatus} ///
          uploadAttachmentRequestError={uploadAttachmentRequestError} ///
          uploadedAttachment={uploadedAttachment} ///
          resetUploadAttachmentRequest={handleResetUploadAttachmentRequest} ///
          sendDeleteAttachmentRequest={handleSendDeleteAttachmentRequest} ///
          deleteAttachmentRequestStatus={deleteAttachmentRequestStatus} ///
          deleteAttachmentRequestError={deleteAttachmentRequestError} ///
          deletedAttachmentId={deletedAttachmentId} ///
          resetDeleteAttachmentRequest={handleResetDeleteAttachmentRequest} ///
          onAttachmentsMetaChange={(meta) => {
            setAttachmentsMeta(meta || []);
          }}
          attachmentMsg={true}
          uniqueId={Number(row?.callId) || 0}
          attachmentsClassname={`notes-list-call-logs-attachment ${
            hasExistingNote && !isEditingNote ? 'view-only' : ''
          }`}
          notesTabAttachment={true}
          error={showError?.length > 0}
          errorMessage={showError}
          showEditIcon={hasExistingNote && !isEditingNote}
          onEditClick={() => setIsEditingNote(true)}
          mentionUsersList={getPhoneCallMentionableUsersResponse}
          isCallLogsDrawerListing={true}
          attachmentsInteractionDisabled={hasExistingNote && !isEditingNote}
          attachmentsDownloadDisabled={hasExistingNote && isEditingNote}
        />
      </div>

      <CallLogsOutboundDetailSections
        row={row}
        callLogsProspectProfileSection={callLogsProspectProfileSection}
        userTimeZone={userTimeZone}
        callLogsProspectDrawerListing={callLogsProspectDrawerListing}
      />
    </div>
  );
};

export default memo(CallLogsSingleCallDetail);
